
import { useEffect, useState } from 'react';
import Dashboard from './components/Dashboard/Dashboard';
import Content from './components/Content/Content';
import Sidebar from './components/Sidebar/Sidebar';
import Pricing from './components/Pricing/Pricing';
import SplitTests from './components/SplitTests/SplitTests';
import Help from './components/Help/Help';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut, SignOutButton, useUser, useAuth, UserProfile } from "@clerk/clerk-react";
import SignInPage from './components/SignIn/SignIn';
import Activity from './components/Activity/Activity';
import Home from './components/Home/Home';
import { useRevit } from './contexts/RevitContext'
import './App.css';
import Ideas from './components/Ideas/Ideas';
import { AnalysisProvider } from './contexts/AnalysisContext';
import Integrations from './components/Integrations/Integrations';
import AIChatbot from './components/AIChatbot/AIChatbot';
import DataBot from './components/AIChatbot/DataBot';
import TeamManagement from './components/TeamManagement/TeamManagement';
import UserAccountInfo from './components/UserAccount/UserAccountInfo';
import TrackingAlertsSetting from './components/UserAccount/TrackingAlertsSetting';
import AccountManagement from './components/AccountManagement/AccountManagement';
import SubAccountExpired from './components/SubAccountExpired/SubAccountExpired';
import { dark } from '@clerk/themes'
import { useTheme } from './contexts/ThemeContext';
import Links from './components/Links/Links';
import Icon from './components/Icon';
import VerifySetup from './components/VerifySetup/VerifySetup';
import { FunnelMapper } from './components/FunnelBuilder/FunnelMapper';
import SetupCheckMissing from './components/FunnelBuilder/SetupCheckMissing';
import Audience from './components/Audience/Audience';
import PricingVip from './components/Pricing/PricingVip';
import PastDue from './components/Pricing/PastDue';
// import OnboardingProgressBar from './components/OnboardingProgressBar/OnboardingProgressBar';
// import GettingStarted from './components/GettingStarted/GettingStarted';
import ThemeToggle from './components/ThemeToggle/ThemeToggle';
import SalesTools from './components/RedirectLinks/SalesTools';
import RedirectLinks from './components/RedirectLinks/RedirectLinks';
import ApiKeys from './components/ApiKeys/ApiKeys';
import ConnectMcp from './components/ConnectMcp/ConnectMcp';
import WelcomeBanner from './components/WelcomeBanner/WelcomeBanner';
import YouTubeConnectModal from './components/AnnouncementModal/YouTubeConnectModal';
// import MonthlyReportModal from './components/MonthlyReport/MonthlyReportModal';
import ProUpgradeModal from './components/ProUpgradeModal/ProUpgradeModal';
import Notifications from './components/Notifications/Notifications';
import DemoAutoSignIn, { isDemoHost, DEMO_USER_ID } from './components/Demo/DemoAutoSignIn';
import posthog from 'posthog-js'
import { Menu, X } from 'lucide-react';

const App = () => {
    const { resolvedTheme } = useTheme();
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
    const [showUserProfile, setShowUserProfile] = useState(false);
    const [isDataBotOpen, setIsDataBotOpen] = useState(false);
    const [isSupportOpen, setIsSupportOpen] = useState(false);
    const [isTopMenuOpen, setIsTopMenuOpen] = useState(false);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const { isSignedIn, user } = useUser();
    const { username, freeUsernames, isSecondaryUser, setSubAccounts, setHasSubAccounts, hasSubAccounts, isSubAccountActive, activeSubAccountName, subAccountAccess, primaryUserAccounts, isProModalOpen, setIsProModalOpen } = useRevit();
    const [isSubscribed, setIsSubscribed] = useState(null);
    const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState(null);
    const [isProAccount, setIsProAccount] = useState(false);
    const [isVIPAccount, setIsVIPAccount] = useState(false);
    const [isFounder, setIsFounder] = useState(false);
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
    const [subscriptionPastDue, setSubscriptionPastDue] = useState(null);
    // Billing interval of the user's current active subscription. Comes from
    // /api/check-subscription and is forwarded to ProUpgradeModal so it can
    // default the "match your current billing" toggle correctly.
    // Values: 'month' (monthly) | 'year' (annual) | null (no active sub).
    const [billingInterval, setBillingInterval] = useState(null);
    // const [isOnboardingCompleted, setIsOnboardingCompleted] = useState(false);
    // const [isProgressBarHidden, setIsProgressBarHidden] = useState(() => {
    //     return localStorage.getItem('progressBarHidden') === 'true';
    // });
    const { getToken, userId } = useAuth();
    // The demo site and the app share one Clerk session. Hide the dashboard
    // while that session is the wrong account for this host.
    const hideSharedSession = isDemoHost()
        ? userId !== DEMO_USER_ID
        : userId === DEMO_USER_ID;


    // Identify the signed-in person, not the account they are viewing.
    // Team members and sub-accounts share `username` with the main account, so
    // using it as the distinct id overwrites that person's email.
    useEffect(() => {
      if (isDemoHost()) return;
      if (!user?.id) return;

      const personProperties = {
        is_team_member: isSecondaryUser,
      };
      const email = user.primaryEmailAddress?.emailAddress;
      if (email) personProperties.email = email;
      if (user.fullName) personProperties.name = user.fullName;

      posthog.identify(user.id, personProperties);

      if (!username) return;

      posthog.group('account', username, {
        is_sub_account: isSubAccountActive,
        ...(activeSubAccountName ? { name: activeSubAccountName } : {}),
      });
    }, [user, username, isSecondaryUser, isSubAccountActive, activeSubAccountName])

    // Global listener for pro-badge clicks
    useEffect(() => {
      const handleGlobalClick = (e) => {
        const badgeClasses = [
          '.pro-badge', 
          '.ob-pro-badge', 
          '.data-pro-badge', 
          '.ai-insights-pro-badge', 
          '.pro-badge-analysis', 
          '.funnels-pro-badge', 
          '.offer-form-pro-badge', 
          '.conversion-emails-pro-badge'
        ];
        
        if (badgeClasses.some(cls => e.target.closest(cls))) {
          e.preventDefault();
          e.stopPropagation();
          setIsProModalOpen(true);
        }
      };

      document.addEventListener('click', handleGlobalClick, true); // Use capture to intercept clicks on disabled links etc
      return () => document.removeEventListener('click', handleGlobalClick, true);
    }, [setIsProModalOpen]);

    
    // Check for onboarding state from localStorage
    // const isOnboardingActive = !isOnboardingCompleted;
    // const currentStep = parseInt(localStorage.getItem('currentOnboardingStep') || '1', 10);

    // Load completed steps from localStorage
    // const [completedSteps, setCompletedSteps] = useState(() => {
    //     const saved = localStorage.getItem('onboardingCompletedSteps');
    //     if (saved) {
    //         try {
    //             const parsed = JSON.parse(saved);
    //             return new Set(parsed);
    //         } catch (e) {
    //             return new Set();
    //         }
    //     }
    //     return new Set();
    // });

    // Listen for changes to localStorage to update completedSteps
    // useEffect(() => {
    //     const handleStorageChange = () => {
    //         const saved = localStorage.getItem('onboardingCompletedSteps');
    //         if (saved) {
    //             try {
    //                 const parsed = JSON.parse(saved);
    //                 setCompletedSteps(new Set(parsed));
    //             } catch (e) {
    //                 setCompletedSteps(new Set());
    //             }
    //         }
    //     };

    //     // Listen for storage events (from other tabs/windows)
    //     window.addEventListener('storage', handleStorageChange);

    //     // Also check periodically for updates in the same tab
    //     const interval = setInterval(handleStorageChange, 500);

    //     return () => {
    //         window.removeEventListener('storage', handleStorageChange);
    //         clearInterval(interval);
    //     };
    // }, []);

    // Listen for progress bar visibility changes
    // useEffect(() => {
    //     const handleVisibilityChange = () => {
    //         setIsProgressBarHidden(localStorage.getItem('progressBarHidden') === 'true');
    //     };

    //     window.addEventListener('progressBarVisibilityChange', handleVisibilityChange);

    //     return () => {
    //         window.removeEventListener('progressBarVisibilityChange', handleVisibilityChange);
    //     };
    // }, []);

    const openBillingPortal = async () => {
        const token = await getToken();

      try {
          const response = await fetch(`https://${SERVER_URL}/api/create-billing-portal-session`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ userId: username }), // Replace 'currentUserId' with actual user ID
          });

          if (!response.ok) {
              throw new Error('Failed to create billing portal session');
          }

          const { url } = await response.json();
          window.open(url, '_blank');
      } catch (error) {
          console.error('Error opening billing portal:', error);
      }
  };

    const handleCancelSubscription = async () => {
        if (!window.confirm(`Are you sure you want to cancel your subscription? Your access wil expire on ${new Date(subscriptionExpiresAt).toLocaleDateString()}`)) {
            return;
        }

        const token = await getToken();

        try {
            const response = await fetch(`https://${SERVER_URL}/api/cancel-subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ userId: username }),
            });

            if (!response.ok) {
                throw new Error('Failed to cancel subscription');
            }

            await response.json();
            alert('Subscription cancelled successfully. It will remain active until the end of your billing period.');
            
            // Refresh subscription data
            window.location.reload();
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            alert('Failed to cancel subscription. Please try again.');
        }
    };

    const handleRenewSubscription = async () => {
        const token = await getToken();

        try {
            const response = await fetch(`https://${SERVER_URL}/api/renew-subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ userId: username }),
            });

            if (!response.ok) {
                throw new Error('Failed to renew subscription');
            }

            await response.json();
            alert('Subscription renewed successfully!');
            
            // Refresh subscription data
            window.location.reload();
        } catch (error) {
            console.error('Error renewing subscription:', error);
            alert('Failed to renew subscription. Please try again.');
        }
    };

    const fetchSubAccountsForFreeUser = async () => {
        try {
            const token = await getToken();
            const response = await fetch(`https://${SERVER_URL}/api/get-sub-accounts/${username}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const subAccountData = await response.json();
                setHasSubAccounts(subAccountData.hasSubAccounts);
                setSubAccounts(subAccountData.subAccounts);
            }
        } catch (error) {
            console.error('Error fetching sub-accounts:', error);
        }
    };

    useEffect(() => {
        const checkSubscription = async () => {
            const token = await getToken();
            if (subscriptionExpiresAt) {
              const now = new Date();
              const expiresAt = new Date(subscriptionExpiresAt);


              if (expiresAt > now) {
                setIsSubscribed('SUBSCRIBED');
                return;
              } else {
                setIsSubscribed('NOT_SUBSCRIBED');
                // createCheckoutSession()
              }
            }

            if (isSignedIn && user && username) {
                try {
                    // Check if sub-account is active from localStorage
                    const isSubAccountActiveFromStorage = localStorage.getItem('isSubAccountActive') === 'true';
                    
                    // Use different endpoint based on sub-account status
                    const endpoint = isSubAccountActiveFromStorage 
                        ? `https://${SERVER_URL}/api/check-sub-account-subscription/${username}`
                        : `https://${SERVER_URL}/api/check-subscription/${username}`;
                    
                    const response = await fetch(endpoint, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) {
                        throw new Error('Failed to check subscription status');
                    }
                    const subscriptionData = await response.json();
                    subscriptionData.isSubscribed ? setIsSubscribed('SUBSCRIBED') : setIsSubscribed('NOT_SUBSCRIBED');
                    setSubscriptionExpiresAt(subscriptionData.subscriptionExpiresAt);
                    setIsProAccount(subscriptionData.plan === 'pro' || subscriptionData.plan === 'vip');
                    setIsVIPAccount(subscriptionData.plan === 'vip' || username === 'user_2o7bLOJ3puECQcBuHxznWsXLbyI');
                    setIsFounder(subscriptionData.isFounder);
                    setHasSubAccounts(subscriptionData.hasSubAccounts);
                    setSubAccounts(subscriptionData.subAccounts);
                    setCancelAtPeriodEnd(subscriptionData.cancelAtPeriodEnd);
                    setSubscriptionPastDue(subscriptionData.subscriptionPastDue);
                    setBillingInterval(subscriptionData.billingInterval || null);
                } catch (error) {
                    console.error('Error checking subscription status:', error);
                    // createCheckoutSession()
                }
            }
        };
        if (!freeUsernames.includes(username)) {
            checkSubscription();
        } else {
            setIsSubscribed('SUBSCRIBED');
            // setIsOnboardingCompleted(true)
            setIsProAccount(true);
            
            // Fetch sub-accounts for free users
            fetchSubAccountsForFreeUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSignedIn, user, username]);

    // useEffect(() => {
    //     // Only redirect if:
    //     // 1. User is signed in
    //     // 2. Onboarding is not completed
    //     // 3. Not already on getting-started page
    //     // 4. Subscription check has completed (isSubscribed is set)
    //     const alreadyRedirected = localStorage.getItem('isAlreadyRedirectedToGettingStarted');
    //     if (isSignedIn && isOnboardingCompleted === false && isSubscribed !== null && !alreadyRedirected) {
    //         const currentPath = window.location.pathname;
    //         if (currentPath !== '/getting-started' && currentPath !== '/privacy-policy') {
    //             window.location.href = '/getting-started';
    //             localStorage.setItem('isAlreadyRedirectedToGettingStarted', 'true');
    //         }
    //     }
    // }, [isOnboardingCompleted, isSignedIn, isSubscribed]);

    // Add body class only when onboarding bar is actually visible
    // if (typeof document !== 'undefined') {
    //     if (isOnboardingActive && isSubscribed === 'SUBSCRIBED' && completedSteps.size < 6 && !isProgressBarHidden) {
    //         document.body.classList.add('onboarding-active');
    //     } else {
    //         document.body.classList.remove('onboarding-active');
    //     }
    // }

    if (window.location.pathname === '/setup-check-missing') {
      return <SetupCheckMissing />;
    }

    return (
      <Router>
        <DemoAutoSignIn />
        {/* Global Onboarding Progress Bar */}
        {/* {isOnboardingActive && isSubscribed === 'SUBSCRIBED' && completedSteps.size < 6 && !isProgressBarHidden && (
          <OnboardingProgressBar
            currentStep={currentStep}
            totalSteps={6}
            completedSteps={completedSteps}
          />
        )} */}

        <header className={isSecondaryUser ? 'has-account-banner' : undefined}>
          {isSecondaryUser && (
            <div className="secondary-user-banner">
              <div className="account-switcher-dropdown">
                <select 
                  className="account-type-selector"
                  onChange={(e) => {
                    if (e.target.value === 'personal') {
                      // Switch to personal space
                      localStorage.setItem('accountMode', 'personal');
                      localStorage.removeItem('activePrimaryUser');
                      // Clear sub-account related localStorage when switching to personal
                      localStorage.removeItem('isSubAccountActive');
                      localStorage.removeItem('activeSubAccountUsername');
                      localStorage.removeItem('activeSubAccountName');
                      window.location.reload();
                    } else if (e.target.value.startsWith('team-')) {
                      // Switch to specific team member space
                      const primaryUserId = e.target.value.replace('team-', '');
                      localStorage.setItem('accountMode', 'team');
                      localStorage.setItem('activePrimaryUser', primaryUserId);
                      localStorage.removeItem('isSubAccountActive');
                      localStorage.removeItem('activeSubAccountUsername');
                      localStorage.removeItem('activeSubAccountName');
                      window.location.reload();
                    } else {
                      // Switch to team member space (legacy)
                      localStorage.setItem('accountMode', 'team');
                      localStorage.removeItem('activePrimaryUser');
                      localStorage.removeItem('isSubAccountActive');
                      localStorage.removeItem('activeSubAccountUsername');
                      localStorage.removeItem('activeSubAccountName');
                      window.location.reload();
                    }
                  }}
                  value={
                    localStorage.getItem('accountMode') === 'personal' 
                      ? 'personal' 
                      : localStorage.getItem('activePrimaryUser') 
                        ? `team-${localStorage.getItem('activePrimaryUser')}`
                        : `team-${primaryUserAccounts[0]}`
                  }
                >
                  {primaryUserAccounts.map((primaryUserId, index) => (
                    <option key={primaryUserId} value={`team-${primaryUserId}`}>
                      Team Member Account {index + 1}
                    </option>
                  ))}
                  {user?.publicMetadata?.stripeCustomerId && (
                    <option value="personal">Personal Account</option>
                  )}
                </select>
              </div>
            </div>
          )}
          <SignedOut>
            {isDemoHost() ? null : (
            <Routes>
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/connect" element={<Navigate to={`/sign-in?redirect_url=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`} />} />
                <Route path="*" element={<Navigate to="/sign-in" />} />
            </Routes>
            )}
          </SignedOut>
          <SignedIn>
            {hideSharedSession ? null : <>
            {isSubscribed === null && (
              // Loading state - show minimal user info and logout
              <div className="loading-user-section">
                <div className="loading-user-info">
                  <span className="loading-user-email">{user?.primaryEmailAddress?.emailAddress}</span>
                  <SignOutButton>
                    <Icon name="logout" size={20} color="white" style={{ cursor: 'pointer' }} />
                  </SignOutButton>
                </div>
              </div>
            )}
            {showUserProfile && (
              <div 
                className="user-profile-overlay"
                onClick={() => setShowUserProfile(false)}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <UserProfile 
                    appearance={{
                      baseTheme: resolvedTheme === 'dark' ? dark : undefined,
                    }}
                  >
                    <UserProfile.Page 
                      label="Manage Subscription" 
                      labelIcon={<span>💳</span>}
                      url="manage-subscription"
                    >
                      <div style={{ padding: '20px' }}>
                        {isVIPAccount ? (
                          <>
                            <h3>Subscription Management</h3>
                            <div style={{ marginBottom: '20px' }}>
                              <p style={{ marginBottom: '10px' }}>
                                <strong>Status:</strong> {cancelAtPeriodEnd ? `Expiring on ${new Date(subscriptionExpiresAt).toLocaleDateString()}` : (subscriptionExpiresAt && new Date(subscriptionExpiresAt) > new Date() ? 'Active' : 'Expired')}
                              </p>
                              {!cancelAtPeriodEnd && (
                                <p style={{ marginBottom: '10px' }}>
                                  <strong>Next Invoice:</strong> {subscriptionExpiresAt ? new Date(subscriptionExpiresAt).toLocaleDateString() : 'N/A'}
                                </p>
                              )}
                            </div>
                            <button 
                              onClick={cancelAtPeriodEnd ? handleRenewSubscription : handleCancelSubscription}
                              style={{
                                backgroundColor: cancelAtPeriodEnd ? '#16a34a' : '#dc2626',
                                color: 'white',
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              {cancelAtPeriodEnd ? 'Renew Subscription' : 'Cancel Subscription'}
                            </button>
                          </>
                        ) : (
                          <>
                            <h3>Subscription Management</h3>
                            <p style={{ marginBottom: '15px' }}>
                              Current Plan: <strong>{isProAccount ? 'RevTrack Pro' : 'RevTrack Essential'}</strong>
                            </p>
                            <button 
                              onClick={openBillingPortal}
                              style={{
                                backgroundColor: '#2563eb',
                                color: 'white',
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Open Billing Portal
                            </button>
                          </>
                        )}
                      </div>
                    </UserProfile.Page>
                    
                    <UserProfile.Page
                      label="Notifications"
                      labelIcon={<span>✉️</span>}
                      url="notifications"
                    >
                      <TrackingAlertsSetting />
                    </UserProfile.Page>

                    <UserProfile.Page 
                      label="Sign Out" 
                      labelIcon={<span>🚪</span>}
                      url="sign-out"
                    >
                      <div style={{ padding: '20px'}}>
                        <h3>Sign Out</h3>
                        <p>Are you sure you want to sign out of your account?</p>
                        <SignOutButton>
                          <button 
                            style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              padding: '10px 20px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Sign Out
                          </button>
                        </SignOutButton>
                      </div>
                    </UserProfile.Page>
                  </UserProfile>
                </div>
              </div>
            )}
            {isSubscribed === 'SUBSCRIBED' ? (
              <>
              {user && 
                            <WelcomeBanner trainingUrl="https://getrevtrack.com/training" userCreatedAt={user?.createdAt} />

              }
              <Sidebar 
                isOpen={isSidebarOpen} 
                toggleSidebar={toggleSidebar} 
                isProAccount={isProAccount} 
                isFounder={isFounder}
                setShowUserProfile={setShowUserProfile}
                // isOnboardingCompleted={isOnboardingCompleted}
                // setIsOnboardingCompleted={setIsOnboardingCompleted}
              />
                {hasSubAccounts && !(isSecondaryUser && subAccountAccess && subAccountAccess.length === 1) ? (
                  <div className="top-user-info">
                    <button
                      className="top-menu-toggle"
                      onClick={() => setIsTopMenuOpen(v => !v)}
                      aria-label="Toggle menu"
                      aria-expanded={isTopMenuOpen}
                    >
                      {isTopMenuOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                    {isTopMenuOpen && (
                      <div className="top-menu-backdrop" onClick={() => setIsTopMenuOpen(false)} />
                    )}
                    <div className={`top-menu-items${isTopMenuOpen ? ' top-menu-items--open' : ''}`}>
                      <AIChatbot 
                        isVIPAccount={isVIPAccount}
                        isOpen={isSupportOpen}
                        setIsOpen={setIsSupportOpen}
                        onOpen={() => { setIsDataBotOpen(false); }}
                      />
                      <a
                        className="top-affiliate-link"
                        href="https://revtrack.tapfiliate.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Become an affiliate
                      </a>
                      <ThemeToggle />
                      <DataBot 
                        isProAccount={isProAccount} 
                        isFounder={isFounder}
                        isOpen={isDataBotOpen}
                        setIsOpen={setIsDataBotOpen}
                        onOpen={() => { setIsSupportOpen(false); }}
                      />
                      <Notifications />
                      <UserAccountInfo setSubscriptionExpiresAt={setSubscriptionExpiresAt} />
                    </div>
                  </div>
                ) : (
                  <div className="top-user-info">
                    <button
                      className="top-menu-toggle"
                      onClick={() => setIsTopMenuOpen(v => !v)}
                      aria-label="Toggle menu"
                      aria-expanded={isTopMenuOpen}
                    >
                      {isTopMenuOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                    {isTopMenuOpen && (
                      <div className="top-menu-backdrop" onClick={() => setIsTopMenuOpen(false)} />
                    )}
                    <div className={`top-menu-items${isTopMenuOpen ? ' top-menu-items--open' : ''}`}>
                      <AIChatbot 
                        isVIPAccount={isVIPAccount}
                        isOpen={isSupportOpen}
                        setIsOpen={setIsSupportOpen}
                        onOpen={() => { setIsDataBotOpen(false); }}
                      />
                      <a
                        className="top-affiliate-link"
                        href="https://revtrack.tapfiliate.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Become an affiliate
                      </a>
                      <ThemeToggle />
                      <DataBot 
                        isProAccount={isProAccount}
                        isOpen={isDataBotOpen}
                        setIsOpen={setIsDataBotOpen}
                        onOpen={() => { setIsSupportOpen(false); }}
                      />
                      <Notifications />
                    </div>
                  </div>
                )}
                <div className="app-main-content">
                  <AnalysisProvider>
                    <Routes>
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/" element={<Navigate to="/home" />} />
                      {/* <Route path="/getting-started" element={<GettingStarted isOnboardingCompleted={isOnboardingCompleted} setIsOnboardingCompleted={setIsOnboardingCompleted} />} /> */}
                      <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
                      <Route path="/overview" element={<Content isProAccount={isProAccount} isFounder={isFounder} openBillingPortal={openBillingPortal} />} />
                      <Route path="/analytics" element={<Home isProAccount={isProAccount} isFounder={isFounder} />} />
                      <Route path="/audience" element={<Audience />} />
                      <Route path="/home" element={<Home isProAccount={isProAccount} isFounder={isFounder} />} />
                      <Route path="/content" element={<Dashboard />} />
                      {(isProAccount || isFounder) && <Route path="/split-tests" element={<SplitTests />} />}
                      <Route path="/links" element={<Links />} />
                      {(isProAccount || isFounder) && <Route path="/redirect-links" element={<RedirectLinks />} />}
                      <Route path="/code" element={<FunnelMapper isProAccount={isProAccount} isFounder={isFounder} openBillingPortal={openBillingPortal} />} />
                      <Route path="/activity" element={<Activity isProAccount={isProAccount} openBillingPortal={openBillingPortal} />} />
                      {(isProAccount || isFounder) && <Route 
                        path="/ideas" 
                        element={
                            <Ideas isProAccount={isProAccount} openBillingPortal={openBillingPortal} />
                        } 
                      />}
                      {/* <Route path="/manual-sale" element={<ManualSale />} /> */}
                      <Route path="/help" element={<Help />} />
                      <Route path="/integrations" element={<Integrations />} />
                      {isProAccount && <Route path="/team-management" element={<TeamManagement />} />}
                      <Route path="/pricing-aaaa" element={<PricingVip />} />
                      <Route path="/pastdueaaa" element={<PastDue />} />
                      <Route path="/account-management" element={<AccountManagement isVIPAccount={isVIPAccount} />} />
                      <Route path="/verify-setup" element={<VerifySetup />} />
                      {(isProAccount || isFounder) && <Route path="/api-keys" element={<ApiKeys />} />}
                      <Route path="/connect" element={<ConnectMcp isProAccount={isProAccount} isFounder={isFounder} />} />
                    </Routes>
                  </AnalysisProvider>
                  {/* <MonthlyReportModal /> */}
                  {/* <CommentAnalysisAnnouncementModal /> */}
                  <YouTubeConnectModal />
                  <ProUpgradeModal
                    isOpen={isProModalOpen}
                    onClose={() => setIsProModalOpen(false)}
                    onUpgrade={() => {
                        setIsProModalOpen(false);
                        window.location.reload();
                    }}
                    currentBillingInterval={billingInterval}
                  />
                </div>
              </>
            ) : isSubscribed === 'NOT_SUBSCRIBED' && window.location.pathname === '/connect' ? (
              <ConnectMcp isProAccount={isProAccount} isFounder={isFounder} />
            ) : isSubscribed === 'NOT_SUBSCRIBED' ? (
              <>
                {subscriptionPastDue ? (
                  <PastDue 
                    subscriptionId={subscriptionPastDue}
                    openBillingPortal={openBillingPortal}
                  />
                ) : isSubAccountActive ? (
                  <SubAccountExpired 
                    accountName={activeSubAccountName} 
                    username={username}
                  />
                ) : (
                  <Pricing />
                )}
              </>
            ) : (
              <div className="centered-container">
                <p>Loading RevTrack, please wait...</p>
              </div>
            )}
            </>}
          </SignedIn>
        </header>
      </Router>
    );
}

export default App;
